import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import cx from "classnames";
import { Field, Form, Formik } from "formik";
import { MultiDropdownField } from "forms/fields";
import differenceBy from "lodash/differenceBy";
import includes from "lodash/includes";
import PropTypes from "prop-types";
import Asset from "rialto-icons/Asset";
import Delete from "rialto-icons/Delete";
import Edit from "rialto-icons/Edit";
import limitationsSelector from "selectors/limitations";

import FormActions from "components/FormActions";
import hoverable from "components/hoc/hoverable";
import { confirm, Translate } from "utils";

import styles from "./DealHeader.css";

const { t } = new Translate("EnquiryHeader");

const title = (assets, separator = ", ") => assets.map((asset) => asset.name).join(separator);

const DealAssets = ({ assets, isHovered, onUpdate, selectedAssets = [] }) => {
  const [isEdit, setIsEdit] = useState(false);
  const hasAssets = selectedAssets.length > 0;

  const isAssetDealsEnabled = useSelector((state) => {
    const limitations = limitationsSelector(state);

    return includes(limitations, "asset_deals") === false;
  });

  const handleCancel = useCallback(() => {
    setIsEdit(false);
  }, [setIsEdit]);

  const handleDelete = useCallback(() => {
    onUpdate([]);
  }, [onUpdate]);

  const handleEdit = useCallback(() => {
    setIsEdit(true);
  }, [setIsEdit]);

  const handleUpdate = useCallback(
    (values) => {
      const removedAssets = differenceBy(selectedAssets, values.assets, (a) => a.id);
      const assetIDs = values.assets.map((asset) => asset.id);

      if (removedAssets.length > 0) {
        return confirm(() => {
          onUpdate(assetIDs);
          setIsEdit(false);
        }, t("confirm.assetUpdate", { asset: title(removedAssets) }));
      }

      setIsEdit(false);

      return onUpdate(assetIDs);
    },
    [onUpdate, setIsEdit, selectedAssets]
  );

  if (isAssetDealsEnabled) {
    if (!hasAssets) {
      return null;
    }

    return (
      <div className={styles.dealAsset}>
        <Asset />
        <span
          dangerouslySetInnerHTML={{
            __html: title(selectedAssets, "<br />"),
          }}
        />
      </div>
    );
  }

  return (
    <div className={cx(styles.dealAsset, { [styles.hovered]: isHovered })}>
      <Asset />
      {isEdit && (
        <Formik initialValues={{ assets: selectedAssets }} onSubmit={handleUpdate}>
          {() => (
            <Form>
              <Field
                align="left"
                component={MultiDropdownField}
                inputComponentProps={{ value: title(selectedAssets) }}
                keyExtractor={(asset) => asset.id}
                name="assets"
                valueExtractor={(asset) => asset.name}
                values={assets}
                isEdit
              />
              <FormActions onCancel={handleCancel} isEdit isHovered isSaveable withIcon />
            </Form>
          )}
        </Formik>
      )}

      {!isEdit && hasAssets && (
        <>
          <span
            dangerouslySetInnerHTML={{
              __html: title(selectedAssets, "<br />"),
            }}
          />
          <div className={styles.editActions}>
            <button className={styles.btn} onClick={handleEdit}>
              <Edit size="small" />
            </button>
            <button className={styles.btn} onClick={handleDelete}>
              <Delete size="small" />
            </button>
          </div>
        </>
      )}

      {!isEdit && !hasAssets && (
        <button className={cx(styles.btn, styles.new)} onClick={handleEdit}>
          + {t("assignAsset")}
        </button>
      )}
    </div>
  );
};

DealAssets.propTypes = {
  assets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  selectedAssets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  isHovered: PropTypes.bool,
  onUpdate: PropTypes.func.isRequired,
};

export default hoverable(DealAssets);
